import type { AasApiAdapter } from "../adapter";
import { isHttpError, type QueryParams, requestJson } from "../http";
import type {
	ConversationDetail,
	ConversationMessage,
	ConversationSummary,
	ConversationToolCall,
} from "../types";
import type {
	BackendConversation,
	BackendConversationDetail,
	BackendMessage,
	BackendUsage,
} from "./backendTypes";
import { toNumber, toStringRecord } from "./shared";

export function mapConversationSummary(conversation: BackendConversation): ConversationSummary {
	return {
		id: conversation.id,
		title: conversation.title?.trim() || "Untitled conversation",
		profile: conversation.profileSlug ?? "unknown",
		model: conversation.model,
		messageCount: toNumber(conversation.messageCount),
		tokens: toNumber(conversation.totalTokens),
		createdAt: conversation.createdAt,
		lastActivity: conversation.lastMessageAt ?? conversation.updatedAt,
		metadata: toStringRecord(conversation.metadata),
		deploymentId: conversation.bundleId ?? null,
	};
}

/**
 * The wire shape of a stored tool call. Providers agree on the OpenAI envelope,
 * but a row written by an older runtime may be missing any part of it, so every
 * field is proven before it is read rather than asserted through a cast.
 */
function toToolCalls(raw: unknown): ConversationToolCall[] | undefined {
	if (!Array.isArray(raw)) return undefined;

	const calls: ConversationToolCall[] = [];
	for (const entry of raw) {
		if (typeof entry !== "object" || entry === null) continue;
		const { id, function: fn } = entry as { id?: unknown; function?: unknown };
		if (typeof fn !== "object" || fn === null) continue;
		const { name, arguments: args } = fn as { name?: unknown; arguments?: unknown };
		if (typeof name !== "string") continue;
		calls.push({
			id: typeof id === "string" ? id : "",
			name,
			arguments: typeof args === "string" ? args : "",
		});
	}

	return calls.length > 0 ? calls : undefined;
}

function toConversationMessage(message: BackendMessage): ConversationMessage {
	const base: ConversationMessage = {
		id: message.id,
		role: message.role,
		content: message.content ?? "",
		contentParts: (() => {
			if (!message.contentParts) return undefined;
			try {
				const parsed = JSON.parse(message.contentParts);
				return Array.isArray(parsed) ? parsed : undefined;
			} catch {
				return undefined;
			}
		})(),
		createdAt: message.createdAt,
		attachments: message.attachments?.map(({ id, filename, mimeType, sizeBytes, url }) => ({
			id,
			filename,
			mimeType,
			sizeBytes,
			url,
		})),
	};

	if (message.role === "tool") {
		if (message.toolCallId) base.toolCallId = message.toolCallId;
		if (message.name) base.toolName = message.name;
		return base;
	}

	if (message.role === "assistant") {
		const calls = toToolCalls(message.toolCalls);
		if (calls) base.toolCalls = calls;
		if (message.reasoningContent) base.reasoning = message.reasoningContent;
		if (message.model) base.model = message.model;
		if (typeof message.usage?.total === "number") base.tokens = message.usage.total;
	}

	return base;
}

function toConversationMessages(messages: BackendMessage[]): ConversationMessage[] {
	return messages.map(toConversationMessage);
}

async function loadConversationDetail(id: string): Promise<ConversationDetail | null> {
	try {
		const detail = await requestJson<BackendConversationDetail>(
			`/api/conversations/${encodeURIComponent(id)}`,
		);
		const usage = await requestJson<BackendUsage>(
			`/api/usage/conversations/${encodeURIComponent(id)}`,
			{
				query: { granularity: "total" },
			},
		).catch(() => null);

		const summary = mapConversationSummary(detail.conversation);
		return {
			...summary,
			messages: toConversationMessages(detail.messages),
			costUsd: usage?.summary.costUsd ?? 0,
		};
	} catch (error) {
		if (isHttpError(error) && (error.status === 404 || error.status === 400)) {
			return null;
		}
		throw error;
	}
}

export const conversationsApi: AasApiAdapter["conversations"] = {
	async list(filters) {
		const query: QueryParams = {
			profile: filters.profile,
			"metadata.user_id": filters.metadataUserId,
			"metadata.triggerId": filters.metadataTriggerId,
			limit: 50,
			offset: 0,
		};
		const conversations = await requestJson<BackendConversation[]>("/api/conversations", { query });
		return conversations.map(mapConversationSummary);
	},
	async get(id: string) {
		return loadConversationDetail(id);
	},
};
